import { computed } from 'nanostores'

import { customerTierTags, defaultTag } from '@/constants/rewards'
import {
  $customer,
  $subscriptionIdToCampaingsMap,
  $subscriptions,
  $authenticationToken,
} from './stores'
import {
  isMealProgramSubscription,
  isProductSubscription,
} from '@/utilities/sakara'
import type { Subscription } from '@/models/subscription'

export const $rewardsTierTag = computed([$customer], (customer) => {
  if (!customer) {
    return defaultTag
  }

  const { tags } = customer
  const tierTags = Object.values(customerTierTags)

  return tags?.find((tag) => tierTags.includes(tag)) || defaultTag
})

export const $mealProgramSubscriptions = computed(
  [$subscriptions],
  (subscriptions) => {
    return subscriptions.filter(isMealProgramSubscription)
  },
)

export const $productSubscriptions = computed(
  [$subscriptions],
  (subscriptions) => {
    return subscriptions.filter(isProductSubscription)
  },
)

export const $activeSubscriptions = computed(
  [$subscriptions],
  (subscriptions) => {
    return subscriptions.filter((s) => !s.isCancelled)
  },
)

export const $activeMealProgramSubscriptions = computed(
  [$mealProgramSubscriptions],
  (mealProgramSubscriptions) => {
    return mealProgramSubscriptions.filter((s) => !s.isCancelled)
  },
)

export const $activeProductSubscriptions = computed(
  [$productSubscriptions],
  (productSubscriptions) => {
    return productSubscriptions.filter((s) => !s.isCancelled)
  },
)

export const $cancelledSubscriptions = computed(
  [$subscriptions],
  (subscriptions) => {
    return subscriptions.filter((s) => s.isCancelled)
  },
)

export const $cancelledMealProgramSubscriptions = computed(
  [$mealProgramSubscriptions],
  (mealProgramSubscriptions) => {
    return mealProgramSubscriptions.filter((s) => s.isCancelled)
  },
)

export const $cancelledProductSubscriptions = computed(
  [$productSubscriptions],
  (productSubscriptions) => {
    return productSubscriptions.filter((s) => s.isCancelled)
  },
)

export const $hasSubscriptions = computed([$subscriptions], (subscriptions) => {
  return subscriptions.length > 0
})

export const $hasActiveSubscriptions = computed(
  [$activeSubscriptions],
  (activeSubscriptions) => {
    return activeSubscriptions.length > 0
  },
)

export const $hasCancelledSubscriptions = computed(
  [$cancelledSubscriptions],
  (cancelledSubscriptions) => {
    return cancelledSubscriptions.length > 0
  },
)

export const $hasActiveMealProgramSubscriptions = computed(
  [$activeMealProgramSubscriptions],
  (activeMealProgramSubscriptions) => {
    return activeMealProgramSubscriptions.length > 0
  },
)

export const $hasActiveProductSubscriptions = computed(
  [$activeProductSubscriptions],
  (activeProductSubscriptions) => {
    return activeProductSubscriptions.length > 0
  },
)

export const $isSubscriptionInAccount = computed(
  [$subscriptions],
  (subscriptions) => (subscriptionId: number) => {
    return subscriptions.some((s) => s.id === subscriptionId)
  },
)

export const $isSubscriptionActive = computed(
  [$subscriptions],
  (subscriptions) => (subscriptionId: number) => {
    return subscriptions.some((s) => s.id === subscriptionId && !s.isCancelled)
  },
)

export const $getSubscriptionById = computed(
  [$subscriptions],
  (subscriptions) => (subscriptionId: number) => {
    return subscriptions.find((s) => s.id === subscriptionId)
  },
)

export const $isForMealProgram = computed(
  [$getSubscriptionById],
  (getSubscriptionById) => (subscriptionId: number) => {
    const subscription = getSubscriptionById(subscriptionId)
    return isMealProgramSubscription(subscription as Subscription)
  },
)

export const $isForProduct = computed(
  [$getSubscriptionById],
  (getSubscriptionById) => (subscriptionId: number) => {
    const subscription = getSubscriptionById(subscriptionId)
    return isProductSubscription(subscription as Subscription)
  },
)

export const $getSubscriptionByExternalId = computed(
  [$subscriptions],
  (subscriptions) => (externalSubscriptionId: string) => {
    return subscriptions.find(
      (s) => s.externalSubscriptionId === externalSubscriptionId,
    )
  },
)

export const $getCampaignsBySubscriptionId = computed(
  [$subscriptionIdToCampaingsMap],
  (subscriptionIdToCampaingsMap) => (subscriptionId: number) => {
    return subscriptionIdToCampaingsMap[subscriptionId]
  },
)

export const $isLoggedIn = computed(
  [$authenticationToken],
  (authenticationToken) => !!authenticationToken,
)
